/**
 * 🎨 Simple Smart Color Utils
 *
 * Just gets you good colors that work well together and avoids conflicts
 *
 * Usage:
 * import { getColors } from './utils/smartColorUtils';
 * const colors = getColors(8); // Returns 8 perfect colors
 *
 * @version 1.0.0
 */

// 🎨 50 Great colors (trending from Coolors.co)
const ALL_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#1DD1A1',
  '#FFE66D',
  '#8B5CF6',
  '#FF9F43',
  '#747D8C',
  '#FF8E53',
  '#6FAADB',
  '#00D2D3',
  '#32FF7E',
  '#F7931E',
  '#A855F7',
  '#EE5A24',
  '#57606F',
  '#3742FA',
  '#10AC84',
  '#FF9FF3',
  '#18DCFF',
  '#2E86AB',
  '#06D6A0',
  '#F0932B',
  '#2F3542',
  '#55A3FF',
  '#FF006E',
  '#FFB3BA',
  '#118AB2',
  '#D63031',
  '#40407A',
  '#5F27CD',
  '#8338EC',
  '#BAFFC9',
  '#84A59D',
  '#FDCB6E',
  '#706FD3',
  '#54A0FF',
  '#BAE1FF',
  '#A4B0BE',
  '#FFD32A',
  '#FF7675',
  '#74B9FF',
  '#81ECEC',
  '#6C5CE7',
  '#A29BFE',
  '#FD79A8',
  '#E17055',
  '#00B894',
  '#E84393',
  '#9B59B6',
] as const;

// 🚨 Colors to AVOID using together
const CONFLICT_GROUPS = [
  // Similar reds (too close)
  ['#FF6B6B', '#FF8E53', '#EE5A24', '#D63031'],
  // Similar blues (hard to distinguish)
  ['#45B7D1', '#6FAADB', '#54A0FF', '#3742FA'],
  // Similar greens (blend together)
  ['#1DD1A1', '#10AC84', '#32FF7E', '#00B894'],
  // Light colors (poor contrast on light backgrounds)
  ['#FFE66D', '#FFD32A', '#BAFFC9', '#BAE1FF', '#FFB3BA'],
  // Dark colors (poor contrast on dark backgrounds)
  ['#2F3542', '#57606F', '#40407A'],
  // Neon colors (cause eye strain together)
  ['#32FF7E', '#FF006E', '#18DCFF'],
  // Similar purples
  ['#8B5CF6', '#A855F7', '#5F27CD', '#6C5CE7'],
  // Similar pastels
  ['#FF9FF3', '#FD79A8', '#A29BFE'],
];

/**
 * 🔍 Check if two colors conflict
 */
function hasConflict(color1: string, color2: string): boolean {
  return CONFLICT_GROUPS.some(group => group.includes(color1) && group.includes(color2));
}

/**
 * 🧠 Smart color selection - picks colors that don't conflict
 */
function selectColors(count: number): string[] {
  if (count <= 0) return [];

  const selected: string[] = [];
  const available = [...ALL_COLORS];

  // Pick first color (best one)
  selected.push(available.shift()!);

  // Pick remaining colors that don't conflict
  while (selected.length < count && available.length > 0) {
    let colorPicked = false;

    // Find first color that doesn't conflict with any selected color
    for (let i = 0; i < available.length; i++) {
      const color = available[i];
      const hasAnyConflict = selected.some(selectedColor => hasConflict(color, selectedColor));

      if (!hasAnyConflict) {
        selected.push(color);
        available.splice(i, 1);
        colorPicked = true;
        break;
      }
    }

    // If no safe color found, just pick the first remaining one
    if (!colorPicked && available.length > 0) {
      selected.push(available.shift()!);
    }
  }

  return selected;
}

/**
 * 🚀 MAIN FUNCTIONS
 */

// Get a batch of good colors (main function)
export function getColors(count: number): string[] {
  return selectColors(count);
}

/**
 * 🤖 DYNAMIC COLOR SOLUTIONS (when you don't know the count)
 */

// Create a dynamic color palette that grows as needed
export function createColorPalette() {
  let currentPalette: string[] = [];

  const palette = {
    // Get color by index - automatically expands if needed
    getColor: (index: number): string => {
      // Expand palette if we need more colors
      while (index >= currentPalette.length) {
        const batchSize = Math.min(10, 50 - currentPalette.length);
        const newColors = selectColors(currentPalette.length + batchSize);
        currentPalette = newColors;

        // If we've used all 50 colors, start cycling
        if (currentPalette.length >= 50) break;
      }

      // Return color or cycle if we've used all 50
      return currentPalette[index % currentPalette.length];
    },

    // Get multiple colors at once
    getColors: (count: number): string[] => {
      const colors: string[] = [];
      for (let i = 0; i < count; i++) {
        colors.push(palette.getColor(i));
      }
      return colors;
    },

    // Current palette info
    size: () => currentPalette.length,
    all: () => [...currentPalette],
  };

  return palette;
}

// Simple function - get color by index (auto-expands)
export function getColorByIndex(index: number): string {
  const palette = createColorPalette();
  return palette.getColor(index);
}

// Get colors for your data array (uses data length)
export function getColorsForData<T>(data: T[]): string[] {
  return getColors(data.length);
}

/**
 * 🔧 UTILITY FUNCTIONS
 */

// Check if a group of colors is safe to use together
export function isColorsSafe(colors: string[]): boolean {
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      if (hasConflict(colors[i], colors[j])) {
        return false;
      }
    }
  }
  return true;
}

// Get all available colors
export const getAllColors = (): string[] => [...ALL_COLORS];

// Default export
export default getColors;

/**
 * 📝 USAGE EXAMPLES:
 *
 * // 1. WHEN YOU KNOW HOW MANY COLORS YOU NEED:
 * const colors = getColors(8); // Returns 8 safe colors
 *
 * // 2. WHEN YOU DON'T KNOW (DYNAMIC):
 *
 * // Option A: Use your data length
 * const colors = getColorsForData(myDataArray);
 *
 * // Option B: Dynamic palette (best for changing data)
 * const palette = createColorPalette();
 * // Use anywhere:
 * <Area fill={palette.getColor(index)} />
 *
 * // Option C: Get color by index (simplest)
 * categories.map((item, index) => (
 *   <div style={{ color: getColorByIndex(index) }}>
 *     {item.name}
 *   </div>
 * ))
 *
 * // 3. IN REACT COMPONENTS:
 *
 * // Known data size:
 * const MyChart = ({ categories }: { categories: any[] }) => {
 *   const colors = getColors(categories.length);
 *   return (
 *     <ResponsiveContainer>
 *       {categories.map((cat, i) => (
 *         <Area key={cat.id} fill={colors[i % colors.length]} />
 *       ))}
 *     </ResponsiveContainer>
 *   );
 * };
 *
 * // Dynamic data:
 * const DynamicChart = ({ data }: { data: any[] }) => {
 *   const palette = useMemo(() => createColorPalette(), []);
 *
 *   return (
 *     <ResponsiveContainer>
 *       {data.map((item, index) => (
 *         <Area
 *           key={item.id}
 *           fill={palette.getColor(index)}
 *           stroke={palette.getColor(index)}
 *         />
 *       ))}
 *     </ResponsiveContainer>
 *   );
 * };
 *
 * // Super simple (no setup):
 * const EasyChart = ({ categories }: { categories: any[] }) => {
 *   return (
 *     <ResponsiveContainer>
 *       {categories.map((category, index) => (
 *         <Area
 *           key={category.id}
 *           fill={getColorByIndex(index)}
 *           stroke={getColorByIndex(index)}
 *         />
 *       ))}
 *     </ResponsiveContainer>
 *   );
 * };
 *
 * // 4. UTILITIES:
 *
 * // Check if colors are safe together
 * const isSafe = isColorsSafe(['#FF6B6B', '#1DD1A1']); // true
 * const notSafe = isColorsSafe(['#FF6B6B', '#FF8E53']); // false (similar reds)
 *
 * // Get all 50 colors
 * const allColors = getAllColors();
 */
